import { getArticle, listArticles } from "@/help/registry";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { HelpSearch } from "./HelpSearch";
import { MarkdownArticle } from "./MarkdownArticle";
import { type HelpArticleState, useHelpArticle } from "./useHelpArticle";

export type HelpDialogProps = {
	open: boolean;
	onClose: () => void;
};

/**
 * The help drawer — a right-anchored, full-height **non-modal** native
 * `<dialog>` that slides in from the right while the main content shifts
 * leftward to make room (the layout shift lives in `routes/_app.tsx`).
 *
 * Why non-modal (vs the previous modal-mode `<dialog>` open):
 * - The drawer is meant to be read alongside the page, not block it. The user
 *   keeps clicking around the app while help is open.
 * - `dialog.show()` (the non-modal open API) presents the dialog without a
 *   backdrop and without making the rest of the page inert / focus-trapped.
 *
 * Trade-offs we accept by going non-modal:
 * - We own ESC. `cancel` only fires for modal `<dialog>`, so we attach a
 *   `keydown` listener to `document` while the drawer is open and call
 *   `onClose()` on `Escape`.
 * - We own focus return. The modal open API returns focus to the trigger on
 *   close for free; the non-modal open API does not. We capture
 *   `document.activeElement` when the drawer opens and refocus it on close.
 * - Focus is intentionally NOT trapped inside the drawer. Tab cycles between
 *   the drawer and the page — that is the whole point of going non-modal.
 *
 * The `<dialog>` stays mounted at all times so the slide-in/out transform
 * animates smoothly. We override the browser's default `display: none` on
 * un-`open` dialogs with `block`, then gate visibility on `translate-x-full`
 * + `pointer-events-none` while closed so it doesn't intercept clicks.
 *
 * The component covers all four required states (loading / empty / error /
 * loaded). The `loading` branch is unreachable in Phase 7A because article
 * resolution is synchronous, but the skeleton render stays present so the
 * branch is exercised by tests today and stays correct when Phase 7B swaps in
 * the DB-backed lookup.
 *
 * Search wiring (D3): `HelpSearch` lives in the dialog header. Picking a
 * search result sets `overrideSlug`, which `resolved` reads in preference to
 * the route-driven `articleState`. A "Back to {route default}" button is
 * rendered above the article body whenever an override is active so the user
 * can return to the route-relevant article without retyping.
 */
export function HelpDialog({ open, onClose }: HelpDialogProps) {
	const dialogRef = useRef<HTMLDialogElement | null>(null);
	const previouslyFocusedRef = useRef<HTMLElement | null>(null);
	const titleId = useId();
	const articleState = useHelpArticle();

	// `overrideSlug` is the user-chosen article (search result OR empty-state
	// "Browse all topics" click). When non-null, it wins over the route-driven
	// hook output. Cleared when the dialog closes so the next open lands back
	// on the route-relevant article.
	const [overrideSlug, setOverrideSlug] = useState<string | null>(null);
	const [query, setQuery] = useState("");

	useEffect(() => {
		if (!open) {
			setOverrideSlug(null);
			setQuery("");
		}
	}, [open]);

	// Drive the imperative <dialog> API from the declarative `open` prop using
	// `show()` — the non-modal open API. Capture the previously focused element
	// on open so we can restore focus on close (the browser does this for free
	// only for modal-mode dialogs, which we are deliberately not using).
	useEffect(() => {
		const dlg = dialogRef.current;
		if (!dlg) return;
		if (open) {
			if (!dlg.open) {
				const active = document.activeElement;
				previouslyFocusedRef.current = active instanceof HTMLElement ? active : null;
				dlg.show();
			}
		} else if (dlg.open) {
			dlg.close();
			const prev = previouslyFocusedRef.current;
			if (prev && document.contains(prev)) {
				prev.focus();
			}
			previouslyFocusedRef.current = null;
		}
	}, [open]);

	// We own ESC ourselves — non-modal `<dialog>` does not dispatch `cancel`.
	// Listener is attached to `document` (not the dialog) so it fires even when
	// focus is on the page rather than inside the drawer.
	useEffect(() => {
		if (!open) return;
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === "Escape") {
				e.preventDefault();
				onClose();
			}
		}
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open, onClose]);

	const resolved = useMemo<HelpArticleState>(() => {
		if (overrideSlug == null) return articleState;
		const article = getArticle(overrideSlug);
		if (article == null) return { state: "empty", slug: overrideSlug };
		return { state: "loaded", article };
	}, [overrideSlug, articleState]);

	const headingTitle = resolved.state === "loaded" ? resolved.article.title : "Help";
	const routeDefaultTitle = articleState.state === "loaded" ? articleState.article.title : null;
	const showBackButton = overrideSlug != null && routeDefaultTitle != null;

	const handleSelect = (slug: string) => {
		setOverrideSlug(slug);
		setQuery("");
	};

	// Slide animation: the dialog stays mounted so the transform animates. We
	// force `display: block` (overriding the browser's default `display: none`
	// on un-`open` dialogs) and use `translate-x-full` + `pointer-events-none`
	// to hide it visually + functionally while closed.
	// `inset-auto` resets the user-agent dialog defaults
	// (`left: 0; right: 0; margin: auto`) which would otherwise center a
	// fixed-width non-modal dialog. We then re-pin top + right ourselves.
	const drawerClass = [
		"fixed inset-auto right-0 top-0 h-screen w-[480px] m-0 max-h-screen p-0",
		"bg-white shadow-2xl",
		"block",
		"transition-transform duration-200 ease-out",
		open ? "translate-x-0" : "translate-x-full pointer-events-none",
	].join(" ");

	return (
		<dialog ref={dialogRef} aria-labelledby={titleId} aria-hidden={!open} className={drawerClass}>
			<div className="flex h-full flex-col">
				<header className="space-y-3 border-b border-[#e5e7eb] px-5 py-4">
					<div className="flex items-center justify-between">
						<h2 id={titleId} className="text-base font-semibold text-[#1e1e20]">
							{headingTitle}
						</h2>
						<button
							type="button"
							onClick={onClose}
							aria-label="Close help"
							className="flex h-8 w-8 items-center justify-center rounded-md text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#1e1e20]"
						>
							<span aria-hidden="true">×</span>
						</button>
					</div>
					<HelpSearch query={query} onQueryChange={setQuery} onSelect={handleSelect} />
				</header>

				<div className="flex-1 overflow-y-auto px-5 py-4">
					{showBackButton ? (
						<button
							type="button"
							onClick={() => setOverrideSlug(null)}
							className="mb-3 inline-flex items-center text-xs font-medium text-[#4f46e5] hover:text-[#4338ca]"
						>
							<span aria-hidden="true">←</span>
							<span className="ml-1">Back to {routeDefaultTitle}</span>
						</button>
					) : null}
					<HelpDialogBody state={resolved} onSelectSlug={handleSelect} />
				</div>
			</div>
		</dialog>
	);
}

function HelpDialogBody({
	state,
	onSelectSlug,
}: {
	state: HelpArticleState;
	onSelectSlug: (slug: string) => void;
}) {
	if (state.state === "loading") {
		return <HelpSkeleton />;
	}
	if (state.state === "error") {
		return (
			<div className="space-y-3">
				<p className="text-sm text-[#1e1e20]">Something went wrong loading this article.</p>
				<p className="text-sm text-[#6b7280]">
					<a
						href="mailto:simon@simondegnan.com?subject=Help%20article%20error"
						className="text-[#4f46e5] underline hover:text-[#4338ca]"
					>
						Report this
					</a>
				</p>
			</div>
		);
	}
	if (state.state === "empty") {
		return <HelpEmpty onSelectSlug={onSelectSlug} />;
	}
	return <MarkdownArticle markdown={state.article.body} />;
}

function HelpEmpty({ onSelectSlug }: { onSelectSlug: (slug: string) => void }) {
	const articles = listArticles();
	return (
		<div className="space-y-4">
			<p className="text-sm text-[#1e1e20]">We don't have a help article for this page yet.</p>
			<div>
				<p className="text-xs font-medium uppercase tracking-[0.08em] text-[#9ca3af]">
					Browse all topics
				</p>
				<ul className="mt-2 space-y-1">
					{articles.map((a) => (
						<li key={a.slug}>
							<button
								type="button"
								onClick={() => onSelectSlug(a.slug)}
								className="w-full rounded px-2 py-1.5 text-left text-sm text-[#4f46e5] hover:bg-[#f3f4f6]"
							>
								{a.title}
							</button>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}

function HelpSkeleton() {
	return (
		<div aria-hidden="true" data-testid="help-skeleton" className="space-y-3">
			<div className="h-4 w-2/3 animate-pulse rounded bg-[#f3f4f6]" />
			<div className="h-3 w-full animate-pulse rounded bg-[#f3f4f6]" />
			<div className="h-3 w-5/6 animate-pulse rounded bg-[#f3f4f6]" />
			<div className="h-3 w-4/6 animate-pulse rounded bg-[#f3f4f6]" />
		</div>
	);
}
